import { ChartsTooltipContainer, useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import type { FC } from 'react';
import { yearOfSeriesId, yoyChange, yoyPoints } from './helpers';
import { formatPercentYoY, formatPointsYoY } from './summaries';

// TooltipSeriesItem is the slice of MUI's own series item this module reads, so
// the row builder can be exercised without mounting a chart.
export type TooltipSeriesItem = {
  seriesId: string | number;
  color: string;
  value: number | null;
  formattedValue: string;
  formattedLabel: string | null;
};

export type YearOverYearRow = {
  seriesId: string;
  label: string;
  color: string;
  formattedValue: string;
  // The comparison against the same month a year earlier — a fraction, or a
  // difference in points when the chart already plots percentages. undefined
  // renders as a dash, and means one of: no earlier year on the chart, no
  // comparable number, or a month whose figure isn't final yet.
  change: number | undefined;
};

// The month the chart is part-way through. Its number is a few days of trade
// against a full month a year ago, and month boundaries don't line up year to
// year either, so it gets no comparison at all.
export type PartialMonth = { year: number; index: number };

type BuildRowsInput = {
  seriesItems: TooltipSeriesItem[];
  dataIndex: number;
  partialMonth?: PartialMonth;
  asPoints?: boolean;
};

// buildYearOverYearRows turns the hovered month's series values into one row
// per series — the same rows the chart has always shown — and hangs the
// year-over-year comparison off whichever row carries that year's whole
// number.
export const buildYearOverYearRows = ({
  seriesItems,
  dataIndex,
  partialMonth,
  asPoints = false,
}: BuildRowsInput): YearOverYearRow[] => {
  const years = seriesItems.map((item) => yearOfSeriesId(item.seriesId));

  // One number per year. The current year is drawn as two stacked bars — what
  // has been sold and the estimated remainder — and the month's figure is the
  // two together, which is also the height of the bar being hovered.
  const totals = new Map<number, number>();
  for (const [index, item] of seriesItems.entries()) {
    const year = years[index];
    if (year === undefined || item.value === null) {
      continue;
    }

    totals.set(year, (totals.get(year) ?? 0) + item.value);
  }

  // A year's comparison belongs to one row, or it would be stated twice for
  // the current year. The estimate is drawn after the actual and, whenever it
  // contributes anything, is the series already formatted as the whole month —
  // so the last row of a year carrying a number is the one that gets it.
  const carriers = new Map<number, string | number>();
  for (const [index, item] of seriesItems.entries()) {
    const year = years[index];
    if (year === undefined) {
      continue;
    }

    if (!carriers.has(year) || (item.value !== null && item.value !== 0)) {
      carriers.set(year, item.seriesId);
    }
  }

  const change = asPoints ? yoyPoints : yoyChange;

  return seriesItems.map((item, index) => {
    const year = years[index];
    const isPartial =
      partialMonth !== undefined && year === partialMonth.year && dataIndex === partialMonth.index;
    const carriesYear = year !== undefined && carriers.get(year) === item.seriesId;

    return {
      seriesId: String(item.seriesId),
      label: item.formattedLabel ?? (year === undefined ? '' : String(year)),
      color: item.color,
      formattedValue: item.formattedValue,
      change:
        carriesYear && !isPartial ? change(totals.get(year), totals.get(year - 1)) : undefined,
    };
  });
};

type YearOverYearTooltipProps = {
  partialMonth?: PartialMonth;
  asPoints?: boolean;
};

// YearOverYearTooltip is the dashboard charts' hover card: every plotted year's
// value for the hovered month, and how each compares with the same month a year
// earlier. It replaces MUI's default tooltip, which shows the values and leaves
// the reader to do that arithmetic in their head.
//
// It borrows its layout from HistoricalYearsTooltip, the YTD tile's hover card,
// which answers the same question a year at a time.
export const YearOverYearTooltip: FC<YearOverYearTooltipProps> = ({ partialMonth, asPoints }) => {
  // Narrowed to what these charts plot. Left to its default the hook also
  // admits scatter, whose value is a point rather than a number, and a year's
  // series can't be added up from those.
  const tooltipData = useAxesTooltip<'line' | 'bar'>();
  const axis = tooltipData?.[0];

  const rows =
    axis === undefined
      ? []
      : buildYearOverYearRows({
          seriesItems: axis.seriesItems,
          dataIndex: axis.dataIndex,
          partialMonth,
          asPoints,
        });

  return (
    <ChartsTooltipContainer trigger="axis">
      {axis !== undefined && rows.length > 0 && (
        <div className="w-72 bg-white rounded-md shadow-xl border border-gray-200 p-3 text-sm">
          <div className="font-medium text-gray-900 mb-2 border-b pb-1">
            {axis.axisFormattedValue}
          </div>
          <div className="flex flex-col divide-y divide-gray-200">
            <div className="flex flex-row pb-1">
              <div className="basis-1/3 flex-none text-gray-500 font-medium">Year</div>
              <div className="basis-1/3 flex-none text-gray-500 font-medium">Total</div>
              <div className="basis-1/3 flex-none text-gray-500 font-medium text-right">
                YoY Change
              </div>
            </div>

            {rows.map((row) => (
              <div key={row.seriesId} className="flex flex-row p-1">
                <div className="basis-1/3 flex-none font-semibold tabular-nums flex items-center gap-1.5">
                  <span
                    aria-hidden
                    className="size-2.5 rounded-xs flex-none"
                    style={{ backgroundColor: row.color }}
                  />
                  {row.label}
                </div>
                <div className="basis-1/3 flex-none tabular-nums">{row.formattedValue}</div>
                <div className="basis-1/3 flex-none text-right tabular-nums">
                  {row.change === undefined ? (
                    <span className="text-gray-400">-</span>
                  ) : (
                    <span className={row.change < 0 ? 'text-red-600' : 'text-green-600'}>
                      {asPoints ? formatPointsYoY(row.change) : formatPercentYoY(row.change)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </ChartsTooltipContainer>
  );
};
